import React from 'react';
import { cn } from '@/lib/utils';
import { Switch } from '../../ui/switch';

/**
 * The building blocks of the bot page's side panel, so every tab (Tasks, Memory,
 * Learning, Scheduled, Apps) reads the same: one type scale (13px titles and items,
 * 12px secondary text), one card, one list, one empty state, one field and one button
 * size. Tabs never repeat their own name as a heading.
 */

/** A short line on what the tab is for, with the tab's main actions on the right. */
export const PanelIntro = ({ children, actions }) => (
  <div className="flex items-start gap-3">
    {children && <p className="flex-1 min-w-0 text-xs leading-relaxed text-muted-foreground">{children}</p>}
    {actions && <div className={cn('flex items-center gap-1.5 shrink-0', !children && 'flex-1 flex-wrap justify-end')}>{actions}</div>}
  </div>
);

/** A titled group; `action` sits on the right of the title. */
export const PanelSection = ({ title, count, action, tone, children, className, ...props }) => (
  <section className={cn('space-y-2', className)} {...props}>
    {(title || action) && (
      <div className="flex items-center gap-2 min-h-6">
        {title && (
          <h3 className={cn('text-[13px] font-semibold', tone === 'attention' ? 'text-amber-700 dark:text-amber-400' : 'text-foreground')}>
            {title}
          </h3>
        )}
        {count > 0 && <span className="text-xs text-muted-foreground tabular-nums">{count}</span>}
        {action && <div className="ml-auto flex items-center gap-1">{action}</div>}
      </div>
    )}
    {children}
  </section>
);

/** A card: plain, or `attention` for things waiting on the person. */
export const PanelCard = ({ as: As = 'div', tone, className, children, ...props }) => (
  <As
    className={cn(
      'rounded-xl border p-3',
      tone === 'attention' ? 'border-amber-500/40 bg-amber-500/[0.05]' : 'border-border bg-card',
      className,
    )}
    {...props}
  >
    {children}
  </As>
);

/** Rows in one bordered block. */
export const PanelList = ({ className, children, ...props }) => (
  <ul className={cn('rounded-xl border border-border bg-card divide-y divide-border overflow-hidden', className)} {...props}>
    {children}
  </ul>
);

/** One row of a PanelList: optional icon, title, a secondary line, and trailing controls
 * (`wrap` lets a long title, like a remembered fact, run over several lines). */
export const PanelRow = ({ icon, title, subtitle, trailing, onClick, dimmed, titleAttr, wrap, className }) => {
  const body = (
    <>
      {icon && <span className="mt-0.5 shrink-0 text-muted-foreground">{icon}</span>}
      <span className="flex-1 min-w-0">
        <span className={cn('block text-[13px] text-foreground', wrap ? 'break-words whitespace-pre-line' : 'truncate')} title={titleAttr}>{title}</span>
        {subtitle && <span className="block text-xs text-muted-foreground truncate">{subtitle}</span>}
      </span>
    </>
  );
  return (
    <li className={cn('group flex items-start gap-2 px-3 py-2', dimmed && 'opacity-60', className)}>
      {onClick ? (
        <button type="button" onClick={onClick} className="flex-1 min-w-0 flex items-start gap-2 text-left hover:opacity-80">
          {body}
        </button>
      ) : <div className="flex-1 min-w-0 flex items-start gap-2">{body}</div>}
      {trailing && <span className="shrink-0 flex items-center gap-0.5 self-center">{trailing}</span>}
    </li>
  );
};

/** A fact or rule the person can remove: the X shows on hover (always on touch). */
export const RemoveButton = ({ onClick, title }) => (
  <button type="button" onClick={onClick} title={title} aria-label={title}
    className="p-1 rounded-md text-muted-foreground opacity-0 group-hover:opacity-100 max-md:opacity-100 hover:text-red-500 hover:bg-accent transition">
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
  </button>
);

/** Nothing here yet: an icon, one sentence, and what to do about it. */
export const PanelEmpty = ({ icon: Icon, children, action }) => (
  <div className="rounded-xl border border-dashed border-border px-4 py-5 text-center">
    {Icon && (
      <span className="mx-auto mb-2 flex w-8 h-8 items-center justify-center rounded-full bg-accent text-muted-foreground">
        <Icon className="w-4 h-4" />
      </span>
    )}
    <p className="text-xs leading-relaxed text-muted-foreground">{children}</p>
    {action && <div className="mt-3 flex justify-center">{action}</div>}
  </div>
);

/** A setting that is on or off, as a card with its explanation. */
export const PanelToggle = ({ title, hint, checked, onChange, disabled, ...props }) => (
  <PanelCard as="label" className="flex items-start gap-3 cursor-pointer" {...props}>
    <span className="flex-1 min-w-0">
      <span className="block text-[13px] font-medium text-foreground">{title}</span>
      {hint && <span className="block mt-0.5 text-xs text-muted-foreground">{hint}</span>}
    </span>
    <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} />
  </PanelCard>
);

/** The one text field style in the panel (`panelField`: full width; `panelInput` + a width class otherwise). */
export const panelInput = 'h-8 rounded-lg border border-input bg-background px-3 text-[13px] text-foreground '
  + 'placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-amber-500/60';
export const panelField = `w-full ${panelInput}`;

/** Button classes for the panel: secondary (outline) and primary (honey). Used with the Button component. */
export const panelButton = 'h-8 px-3 gap-1.5 text-xs';
export const panelPrimary = `${panelButton} bg-amber-500 text-honey-ink hover:bg-amber-400 font-semibold`;
export const panelIconButton = 'inline-flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors';
